import { useEffect } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Building2, Users as UsersIcon, ShieldCheck, FileText, Settings as GearIcon,
  Trash2, KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const SECTIONS = [
  { to: "profile",     label: "Garage Profile",      Icon: Building2,
    desc: "Business details on your documents.",
    roles: ["owner", "branch_manager"] },
  { to: "users",       label: "Users",               Icon: UsersIcon,
    desc: "Add, edit and manage team access.",
    roles: ["owner", "branch_manager"] },
  { to: "roles",       label: "Roles",               Icon: ShieldCheck,
    desc: "The 10 Fender roles and their access areas.",
    roles: ["owner", "branch_manager"] },
  { to: "documents",   label: "Document Settings",   Icon: FileText,
    desc: "Numbering, GST defaults, terms and payment details.",
    roles: ["owner"] },
  { to: "preferences", label: "General Preferences", Icon: GearIcon,
    desc: "Language, date/time, working hours, workshop rules.",
    roles: ["owner", "branch_manager"] },
  { to: "change-password", label: "Change Password", Icon: KeyRound,
    desc: "Update your login password.",
    roles: ["owner", "branch_manager", "service_advisor", "advisor",
            "mechanic", "storekeeper", "accountant", "quality_inspector",
            "receptionist", "customer_relations", "cashier", "driver",
            "purchase_executive", "security"] },
  { to: "delete-account", label: "Delete Account",   Icon: Trash2,
    desc: "Request permanent deletion of your account.",
    roles: ["owner", "branch_manager", "service_advisor", "advisor",
            "mechanic", "storekeeper", "accountant", "quality_inspector",
            "receptionist", "customer_relations"] },
];

export default function SettingsLayout() {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  const allowed = SECTIONS.filter((s) => s.roles.includes(user?.role));
  const active = loc.pathname.replace(/^\/settings\/?/, "").split("/")[0] || "profile";

  useEffect(() => {
    if (loc.pathname === "/settings" || loc.pathname === "/settings/") {
      nav("/settings/profile", { replace: true });
    }
  }, [loc.pathname, nav]);

  return (
    <div className="space-y-4" data-testid="settings-page">
      <header>
        <h1 className="text-[22px] md:text-[26px] font-bold text-[#1d3557] leading-tight">Settings</h1>
        <p className="text-[12.5px] text-[#667085] mt-0.5">
          Configure what materially affects workshop operations and documents.
        </p>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-[240px_1fr] gap-4">
        {/* Left menu — desktop */}
        <aside className="hidden md:block" data-testid="settings-menu">
          <nav className="bg-white rounded-xl border border-[#E4E7EC] p-1.5 sticky top-4">
            {allowed.map((s) => (
              <NavLink
                key={s.to}
                to={`/settings/${s.to}`}
                data-testid={`settings-nav-${s.to}`}
                className={({ isActive }) =>
                  `flex items-center gap-2.5 px-3 py-2.5 rounded-md text-[13px] font-semibold transition-colors ${
                    isActive || active === s.to
                      ? "bg-[#1d3557] text-white"
                      : "text-[#667085] hover:text-[#1d3557] hover:bg-[#F1FAEE]"
                  }`
                }
              >
                <s.Icon size={13} /> {s.label}
              </NavLink>
            ))}
          </nav>
        </aside>

        {/* Mobile pill row */}
        <nav className="md:hidden flex gap-1 overflow-x-auto bg-white rounded-lg border border-[#E4E7EC] p-1"
             data-testid="settings-menu-mobile">
          {allowed.map((s) => (
            <NavLink key={s.to} to={`/settings/${s.to}`}
                     className={({ isActive }) =>
                       `h-9 px-3 rounded-md text-[11.5px] font-semibold whitespace-nowrap ${
                         isActive || active === s.to
                           ? "bg-[#1d3557] text-white" : "text-[#667085]"
                       }`}>
              {s.label}
            </NavLink>
          ))}
        </nav>

        <section>
          <Outlet />
        </section>
      </div>
    </div>
  );
}

// Helper: dirty-save bar shared across all settings pages.
export function SaveBar({ dirty, saving, onSave, testId }) {
  return (
    <div className="sticky bottom-0 md:static bg-white md:bg-transparent md:border-0 border-t border-[#E4E7EC] px-4 md:px-0 py-3 flex items-center justify-end gap-2"
         data-testid={testId || "settings-save-bar"}>
      <span className="text-[11.5px] text-[#667085] mr-auto">
        {dirty ? "You have unsaved changes." : "All changes saved."}
      </span>
      <button type="button" onClick={onSave} disabled={!dirty || saving}
              data-testid="settings-save-btn"
              className="h-10 px-5 rounded-md bg-[#e63946] hover:bg-[#d02f3c] text-white text-[12.5px] font-semibold disabled:opacity-40 inline-flex items-center gap-1.5">
        {saving ? "Saving…" : "Save Changes"}
      </button>
    </div>
  );
}
